import { ApiService } from '../apiService'
import type {
  FriendDto,
  FriendRequestDto,
  FriendsResponse,
  AddFriendRequest,
  BlockUserRequest,
  GroupDto,
  GroupDetailsDto,
  CreateGroupRequest,
  UpdateGroupRequest,
  AddGroupMemberRequest,
  GroupRole,
  GroupPermissionSettingsDto,
  UpdateGroupPermissionsRequest,
  GenerateShareCodeRequest,
  ShareCodeResponse,
  ShareCodeResponseDto,
  ShareCodeValidationResponse,
  GroupJoinRequestDto,
  GroupBannedUserDto,
  UserGroupBlockDto,
  GroupSearchRequest,
  GroupSearchResponse,
  AdminGroupsResponse,
  AdminGroupsListParams,
} from '../types/social'
import { getGroupRoleName } from '../types/social'

export const socialApi = {
  // ====================
  // FRIENDS
  // ====================

  // Get current user's friends and pending requests
  getFriends: () => ApiService.get<FriendsResponse>('/api/me/friends'),

  // Send a friend request (add friend)
  sendFriendRequest: (request: AddFriendRequest) =>
    ApiService.post<void>('/api/me/friends', request),

  // Accept a friend request
  acceptFriendRequest: (connectionId: string) =>
    ApiService.post<void>(`/api/me/friends/requests/${connectionId}/accept`),

  // Decline a friend request
  rejectFriendRequest: (connectionId: string) =>
    ApiService.post<void>(`/api/me/friends/requests/${connectionId}/decline`),

  // Remove a friend
  removeFriend: (friendUserId: string) =>
    ApiService.delete('/api/me/friends', { friendId: friendUserId }),

  // Get blocked users
  getBlockedUsers: () =>
    ApiService.get<any[]>('/api/me/blocked'),

  // Block a user
  blockUser: (request: BlockUserRequest) =>
    ApiService.post<void>(`/api/me/block/${request.userId}`),

  // Unblock a user
  unblockUser: (userId: string) =>
    ApiService.delete(`/api/me/block/${userId}`),

  // ====================
  // GROUPS
  // ====================

  // Get groups the current user is a member of
  getMyGroups: () => ApiService.get<GroupDto[]>('/api/me/groups'),

  // Get all public groups (for discovery) - DEPRECATED: use searchGroups instead
  getAllGroups: () => ApiService.get<GroupDto[]>('/api/groups'),

  // Get all groups with admin-level data (paginated)
  getAdminGroups: (params: AdminGroupsListParams) => {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString())
    if (params.search) queryParams.append('search', params.search)
    if (params.visibility !== undefined) queryParams.append('visibility', params.visibility.toString())
    if (params.isOfficial !== undefined) queryParams.append('isOfficial', params.isOfficial.toString())
    if (params.isArchived !== undefined) queryParams.append('isArchived', params.isArchived.toString())
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortDescending !== undefined) queryParams.append('sortDescending', params.sortDescending.toString())
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : ''
    return ApiService.get<AdminGroupsResponse>(`/admin/groups${queryString}`)
  },

  // Mark a group official (admin only). Its adventures' leaderboards go live without waiting for
  // the minimum players and ratings, and the creator's friends and group-mates earn full points on
  // them — from the next leaderboard recompute.
  setGroupOfficial: (groupId: string, isOfficial: boolean) =>
    ApiService.put<void>(`/admin/groups/${groupId}/official`, { isOfficial }),

  // Search groups with advanced filters (for discovery)
  searchGroups: (request: GroupSearchRequest) =>
    ApiService.post<GroupSearchResponse>('/api/groups/search/advanced', request),

  // Get group details by ID
  getGroup: (groupId: string) => ApiService.get<GroupDetailsDto>(`/api/groups/${groupId}`),

  // Create a new group
  createGroup: (request: CreateGroupRequest) =>
    ApiService.post<GroupDto>('/api/groups', request),

  // Update a group
  updateGroup: (groupId: string, request: UpdateGroupRequest) =>
    ApiService.put<GroupDto>(`/api/groups/${groupId}`, request),

  // Delete a group
  deleteGroup: (groupId: string) => ApiService.delete(`/api/groups/${groupId}`),

  // Get group invitations for current user
  getMyGroupInvites: () =>
    ApiService.get<any[]>('/api/groups/my-invites'),

  // Accept group invitation
  acceptGroupInvite: (groupId: string) =>
    ApiService.post<void>(`/api/groups/${groupId}/invites/accept`),

  // Decline group invitation
  declineGroupInvite: (groupId: string) =>
    ApiService.post<void>(`/api/groups/${groupId}/invites/decline`),

  // Join a group (unified endpoint - handles direct join and join requests automatically)
  joinGroup: (groupId: string) =>
    ApiService.post<{
      success: boolean
      joinedDirectly: boolean
      joinRequestCreated: boolean
      joinRequestId: string | null
      message: string
    }>(`/api/groups/${groupId}/join`),

  // Cancel a pending join request (for users)
  cancelJoinRequest: (groupId: string, requestId: string) =>
    ApiService.delete(`/api/groups/join-requests/${requestId}`),

  // Get pending join requests for a group (admin/moderator only)
  getGroupJoinRequests: (groupId: string) =>
    ApiService.get<GroupJoinRequestDto[]>(`/api/groups/${groupId}/join-requests`),

  // Approve a join request (admin/moderator only)
  approveJoinRequest: (requestId: string, reviewNotes?: string) =>
    ApiService.post<void>(`/api/Groups/join-requests/${requestId}/approve`, { reviewNotes: reviewNotes || '' }),

  // Reject a join request (admin/moderator only)
  rejectJoinRequest: (requestId: string, reviewNotes?: string) =>
    ApiService.post<void>(`/api/Groups/join-requests/${requestId}/reject`, { reviewNotes: reviewNotes || '' }),

  // Get user's own pending join requests
  getMyJoinRequests: () =>
    ApiService.get<GroupJoinRequestDto[]>('/api/groups/my-join-requests'),

  // Leave a group
  leaveGroup: (groupId: string) =>
    ApiService.post<void>(`/api/Groups/${groupId}/leave`),

  // Get banned users for a group (admin/moderator only)
  getGroupBans: (groupId: string) =>
    ApiService.get<GroupBannedUserDto[]>(`/api/Groups/${groupId}/bans`),

  // Ban a user from a group (admin/moderator only)
  banUser: (groupId: string, userId: string) =>
    ApiService.post<void>(`/api/Groups/${groupId}/ban/${userId}`),

  // Unban a user from a group (admin/moderator only)
  unbanUser: (groupId: string, userId: string) =>
    ApiService.delete(`/api/Groups/${groupId}/ban/${userId}`),

  // Get groups blocked by current user
  getBlockedGroups: () =>
    ApiService.get<UserGroupBlockDto[]>('/api/Groups/blocked'),

  // Block a group (prevent invites)
  blockGroup: (groupId: string) =>
    ApiService.post<void>(`/api/Groups/${groupId}/block`),

  // Unblock a group
  unblockGroup: (groupId: string) =>
    ApiService.delete(`/api/Groups/${groupId}/block`),

  // Add a member to a group (admin/owner only)
  addGroupMember: (groupId: string, request: AddGroupMemberRequest) =>
    ApiService.post<void>(`/api/groups/${groupId}/members`, request),

  // Remove a member from a group (admin/owner only)
  removeGroupMember: (groupId: string, userId: string) =>
    ApiService.delete(`/api/groups/${groupId}/members/${userId}`),

  // Update a member's role (owner/admin only)
  updateMemberRole: (groupId: string, userId: string, role: GroupRole) =>
    ApiService.put<void>(`/api/groups/${groupId}/members/${userId}/role`, {
      role: getGroupRoleName(role)
    }),

  // Update group permissions (owner/admin only)
  updateGroupPermissions: (groupId: string, request: UpdateGroupPermissionsRequest) =>
    ApiService.put<GroupPermissionSettingsDto>(`/api/groups/${groupId}/permissions`, request),

  // Generate a share code for invites (admin/owner only)
  generateShareCode: (groupId: string, request: GenerateShareCodeRequest) =>
    ApiService.post<ShareCodeResponse>(`/api/groups/${groupId}/generate-share-code`, request),

  // Get all active share codes for a group (admin/owner only)
  getGroupShareCodes: (groupId: string) =>
    ApiService.get<ShareCodeResponseDto[]>(`/api/groups/${groupId}/share-codes`),

  // Deactivate a specific share code (admin/owner only)
  deactivateShareCode: (groupId: string, shareCode: string) =>
    ApiService.delete(`/api/groups/${groupId}/share-codes/${shareCode}`),

  // Join a group using a share code
  // The code goes in the body (JoinByShareCodeDto); there is no /groups/join/{code} route.
  joinGroupWithShareCode: (shareCode: string) =>
    ApiService.post<void>('/api/groups/join-by-code', { shareCode }),

  // ====================
  // SHARE CODE VALIDATION
  // ====================

  // Validate share code (unauthenticated - public endpoint) - LEGACY
  validateShareCode: (code: string) =>
    ApiService.get<ShareCodeValidationResponse>(`/api/auth/sharecode/validate/${code}`),

  // Validate share code (authenticated - checks user access) - LEGACY
  validateShareCodeAuthenticated: (code: string) =>
    ApiService.get<ShareCodeValidationResponse>(`/api/auth/me/sharecode/validate/${code}`),

  // ====================
  // SHARE CODE PROCESSING (NEW)
  // ====================

  // Process/validate a share code (resolves the code and performs action)
  processShareCode: (code: string) =>
    ApiService.get<import('../types/social').ShareCodeProcessResponse>(`/api/sharecode/${code}`),

  // Get share code info without resolving (just get metadata)
  getShareCodeInfo: (code: string) =>
    ApiService.get<import('../types/social').ShareCodeProcessResponse>(`/api/sharecode/${code}/info`),

  // ====================
  // DISCUSSIONS
  // ====================

  // Get all discussions, optionally filtered by groupId, adventureId, or profileUserId
  getDiscussions: (groupId?: string, profileUserId?: string, adventureId?: string) => {
    const params = new URLSearchParams()
    if (groupId) params.append('groupId', groupId)
    if (profileUserId) params.append('profileUserId', profileUserId)
    if (adventureId) params.append('adventureId', adventureId)
    const queryString = params.toString() ? `?${params.toString()}` : ''
    return ApiService.get<import('../types/social').DiscussionDto[]>(`/api/discussions${queryString}`)
  },

  // Get a specific discussion by ID
  getDiscussion: (discussionId: string) =>
    ApiService.get<import('../types/social').DiscussionDto>(`/api/discussions/${discussionId}`),

  // Create a new discussion
  createDiscussion: (request: import('../types/social').CreateDiscussionRequest) =>
    ApiService.post<import('../types/social').DiscussionDto>('/api/discussions', request),

  // Flag a discussion for moderator review
  flagDiscussion: (discussionId: string, request: import('../types/social').FlagPostRequest) =>
    ApiService.post<void>(`/api/discussions/${discussionId}/flag`, request),

  // ====================
  // POSTS
  // ====================

  // Get all posts in a discussion
  getDiscussionPosts: (discussionId: string) =>
    ApiService.get<import('../types/social').PostDto[]>(`/api/discussions/${discussionId}/posts`),

  // Get a specific post by ID
  getPost: (postId: string) =>
    ApiService.get<import('../types/social').PostDto>(`/api/discussions/posts/${postId}`),

  // Create a new post/reply
  createPost: (request: import('../types/social').CreatePostRequest) =>
    ApiService.post<import('../types/social').PostDto>('/api/discussions/posts', request),

  // Update a post's content
  updatePost: (postId: string, request: import('../types/social').UpdatePostRequest) =>
    ApiService.put<import('../types/social').PostDto>(`/api/discussions/posts/${postId}`, request),

  // Delete a post
  deletePost: (postId: string) =>
    ApiService.delete(`/api/discussions/posts/${postId}`),

  // Flag a post for moderator review
  flagPost: (postId: string, request: import('../types/social').FlagPostRequest) =>
    ApiService.post<void>(`/api/discussions/posts/${postId}/flag`, request),

  // Hide a post (moderator only)
  hidePost: (postId: string, reason: string) =>
    ApiService.post<void>(`/api/discussions/posts/${postId}/hide`, { reason }),

  // Unhide a post (moderator only)
  unhidePost: (postId: string) =>
    ApiService.post<void>(`/api/discussions/posts/${postId}/unhide`),

  // Upload an image for use in discussions
  uploadDiscussionImage: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return ApiService.post<import('../types/social').UploadDiscussionImageResponse>(
      '/api/discussions/images',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
  },
}
